import React from "react";
import useEngagementIds from "hooks/engagement/useEngagementIds";
import useEngagementClient from "hooks/engagement/useEngagementClient";

const useBookEngagementHeartbeatExit = (
	bookId,
	{ heartbeatIntervalMs = 15000, minElapsedMs = 5000, minScrollDelta = 5, enabled = true } = {}
) => {
	const { al_sid, al_vid } = useEngagementIds();
	const { send } = useEngagementClient(bookId);

	const heartbeatIntervalRef = React.useRef(null);
	const dwellMsRef = React.useRef(0);
	const lastActiveTsRef = React.useRef(null);
	const maxScrollPctRef = React.useRef(0);
	const lastHeartbeatSentAtRef = React.useRef(0);
	const lastSentScrollPctRef = React.useRef(0);

	React.useEffect(() => {
		if (!enabled || !bookId) return;

		const computeScrollPct = () => {
			const doc = document.documentElement;
			const body = document.body;
			const scrollTop = window.pageYOffset || doc.scrollTop || body.scrollTop || 0;
			const viewport = window.innerHeight || doc.clientHeight;
			const fullHeight = Math.max(
				body.scrollHeight,
				doc.scrollHeight,
				body.offsetHeight,
				doc.offsetHeight,
				body.clientHeight,
				doc.clientHeight
			);
			const denom = Math.max(fullHeight - viewport, 1);
			const pct = Math.min(100, Math.max(0, Math.round((scrollTop / denom) * 100)));
			return pct;
		};

		const sendHeartbeatIfNeeded = (force = false) => {
			if (!navigator.onLine) return;
			if (document.visibilityState !== "visible" && !force) return;

			const now = Date.now();
			let dwell = dwellMsRef.current;
			if (lastActiveTsRef.current) dwell += now - lastActiveTsRef.current;

			const elapsedSinceLast = now - (lastHeartbeatSentAtRef.current || 0);
			const currentScroll = Math.round(maxScrollPctRef.current);
			const scrollDelta = Math.abs(currentScroll - (lastSentScrollPctRef.current || 0));

			if (!force && elapsedSinceLast < minElapsedMs && scrollDelta < minScrollDelta) return;

			send({
				event: "heartbeat",
				al_sid,
				al_vid,
				dwell_ms: Math.min(dwell, 86400000),
				scroll_pct: currentScroll,
			});
			lastHeartbeatSentAtRef.current = now;
			lastSentScrollPctRef.current = currentScroll;
		};

		const onScroll = () => {
			const pct = computeScrollPct();
			if (pct > maxScrollPctRef.current) maxScrollPctRef.current = pct;
		};

		const onVisible = () => {
			lastActiveTsRef.current = Date.now();
			if (!heartbeatIntervalRef.current) {
				heartbeatIntervalRef.current = window.setInterval(() => {
					sendHeartbeatIfNeeded(false);
				}, heartbeatIntervalMs);
			}
			window.setTimeout(() => sendHeartbeatIfNeeded(true), 1000);
		};

		const onHidden = () => {
			if (lastActiveTsRef.current) {
				dwellMsRef.current += Date.now() - lastActiveTsRef.current;
				lastActiveTsRef.current = null;
			}
			if (heartbeatIntervalRef.current) {
				window.clearInterval(heartbeatIntervalRef.current);
				heartbeatIntervalRef.current = null;
			}
			send({
				event: "exit",
				al_sid,
				al_vid,
				dwell_ms: Math.min(dwellMsRef.current, 86400000),
				scroll_pct: Math.round(maxScrollPctRef.current),
			});
		};

		const onVisibilityChange = () => {
			if (document.visibilityState === "visible") onVisible();
			else onHidden();
		};

		maxScrollPctRef.current = computeScrollPct();
		if (document.visibilityState === "visible") onVisible();

		window.addEventListener("scroll", onScroll, { passive: true });
		document.addEventListener("visibilitychange", onVisibilityChange);

		return () => {
			window.removeEventListener("scroll", onScroll);
			document.removeEventListener("visibilitychange", onVisibilityChange);
			if (heartbeatIntervalRef.current) {
				window.clearInterval(heartbeatIntervalRef.current);
				heartbeatIntervalRef.current = null;
			}
			let dwell = dwellMsRef.current;
			if (lastActiveTsRef.current) dwell += Date.now() - lastActiveTsRef.current;
			send({
				event: "exit",
				al_sid,
				al_vid,
				dwell_ms: Math.min(dwell, 86400000),
				scroll_pct: Math.round(maxScrollPctRef.current),
			});
		};
	}, [enabled, bookId, al_sid, al_vid, send, heartbeatIntervalMs, minElapsedMs, minScrollDelta]);
};

export default useBookEngagementHeartbeatExit;
